import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import type { ProductFeature as ProductFeatureSchema } from '@amplience/content-types/typings/c-product-feature';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { FlexibleTextPartial } from '@storefront/lib-global/partials/flexibleText';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import { TypographyStyles, TypographyStylesType, colours, media, spacing } from '@storefront/lib-global/stylings';
import { Locale } from '@storefront/lib-global/utils/internationalization/localeHelper';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';

export type ProductFeatureTypes = { content: ProductFeatureSchema };

export type ProductFeatureProps = {
  technology: ProductFeatureTypes;
};

type FeatureContentProps = ProductFeatureSchema;

const S = {
  ProductFeature: styled.li`
    flex: 1 1 975px;
    list-style: none;

    @media ${media.greaterThan('lg')} {
      flex: 1 1 215px;
    }
  `,
  ProductFeatureLink: styled.div`
    a {
      color: ${colours.BLACK};
      text-decoration: none;
    }
  `,
  FeatureContentWrapper: styled.div`
    display: flex;
    flex-direction: column;
  `,
  FeatureContentHeader: styled(FlexibleTextPartial)`
    display: flex;
    justify-content: flex-start;
    margin: 0;

    @media ${media.greaterThan('lg')} {
      justify-content: center;
    }
  `,
  FeatureContent: styled.div`
    display: flex;
    margin-top: ${spacing.XS};

    @media ${media.greaterThan('lg')} {
      flex-direction: column;
      margin-top: 0;
      text-align: center;
    }
  `,
  FeatureContentImage: styled.span`
    align-items: flex-start;
    display: flex;
    justify-content: center;
    min-width: 71px;

    @media ${media.greaterThan('lg')} {
      align-items: center;
      height: 72px;
    }
  `,
  FeatureContentRichText: styled(RichTextPartial)`
    display: flex;
    justify-content: center;
    margin: 0;

    a {
      color: revert;
      text-decoration: underline;
    }
  `,
  Link: styled.div`
    &[role='link'] {
      cursor: pointer;
    }
  `,
};

function FeatureContent({ productFeatureHeader, productFeatureRichText, productFeatureImage }: FeatureContentProps) {
  return (
    <S.FeatureContentWrapper>
      <S.FeatureContentHeader
        {...productFeatureHeader}
        defaultTextSize={TypographyStylesType.BODY_MEDIUM_SEMIBOLD}
        defaultTextColor={colours.BLACK}
      />
      <S.FeatureContent>
        <S.FeatureContentImage>
          <ImagePartial
            altText={productFeatureImage?.altText}
            layout="intrinsic"
            src={productFeatureImage?.image?.name ?? ''}
            height={40}
            width={50}
            assetType={productFeatureImage?.assetType}
            defaultHost={productFeatureImage?.image?.defaultHost}
            useOriginalSize={productFeatureImage?.useOriginalSize}
          />
        </S.FeatureContentImage>

        <S.FeatureContentRichText
          text={getLocalizedValue(productFeatureRichText?.richText)}
          fontSize={TypographyStyles.Body.Small.Regular}
        />
      </S.FeatureContent>
    </S.FeatureContentWrapper>
  );
}

export function ProductFeature({ technology }: ProductFeatureProps) {
  const { locale } = useLocaleContext();
  const anchorLink = technology?.content?.productFeatureAnchorLink;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    const isOutlet = locale === Locale.STOREFRONT_OUTLET_LOCALE;
    const link = anchorLink?.externalLink ?? anchorLink?.internalLink;
    const transformedLink = link === anchorLink?.externalLink ? link : `/${isOutlet ? '' : `${locale}/`}${link}`;

    const handleKeydown = (event: KeyboardEvent) => {
      const elementKey = event.target as HTMLElement;

      if (elementKey.tagName === 'A') return;

      if (event.key === 'Enter') {
        window.open(transformedLink, '_blank', 'noopener,noreferrer');
      }
    };

    const handleClick = (event: MouseEvent) => {
      const elementClick = event.target as HTMLElement;

      if (elementClick.tagName === 'A') return;

      window.open(transformedLink, '_blank', 'noopener,noreferrer');
    };

    if (link) {
      element?.addEventListener('click', handleClick);
      element?.addEventListener('keydown', handleKeydown);
    }

    return () => {
      element?.removeEventListener('click', handleClick);
      element?.removeEventListener('keydown', handleKeydown);
    };
  }, [ref, anchorLink, locale]);

  if (Object.keys(technology.content || {}).length === 0) return null;

  const { productFeatureHeader, productFeatureImage, productFeatureRichText, productFeatureAnchorLink } =
    technology.content;

  const featureProps = {
    productFeatureHeader,
    productFeatureRichText,
    productFeatureImage,
  };

  return (
    <S.ProductFeature>
      {productFeatureAnchorLink ? (
        <S.ProductFeatureLink>
          <S.Link ref={ref} role="link" tabIndex={0} aria-label={`${productFeatureHeader?.text}`}>
            <FeatureContent {...(featureProps as FeatureContentProps)} />
          </S.Link>
        </S.ProductFeatureLink>
      ) : (
        <FeatureContent {...(featureProps as FeatureContentProps)} />
      )}
    </S.ProductFeature>
  );
}

export default ProductFeature;
